import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CContainer } from "@coreui/react-pro";
import PublicSiteShell from "../components/public/PublicSiteShell";
import "../styles/public-experience.css";

const values = [
  {
    title: "Fairness",
    text: "Wir stehen für faire Bedingungen, nachvollziehbare Abläufe und einen respektvollen Umgang mit allen Beteiligten. Internationale Rekrutierung soll auf Ausgewogenheit, Transparenz und Verantwortung beruhen.",
  },
  {
    title: "Respekt vor Qualifikation",
    text: "Vorhandene Abschlüsse, Berufserfahrung und Kompetenzen müssen ernst genommen und sauber bewertet werden. Unser Anspruch ist ein Umgang, der Qualifikation anerkennt und Entwicklung realistisch begleitet.",
  },
  {
    title: "Gute Vorbereitung",
    text: "Ein erfolgreicher beruflicher Start in Europa erfordert mehr als einen Arbeitsvertrag. Dazu gehören Kenntnisse über Bewerbung, Unternehmenskultur, Kommunikation, Arbeitsweise und Erwartungen im europäischen Arbeitsumfeld.",
  },
  {
    title: "Verantwortung",
    text: "Internationale Rekrutierung bringt Verantwortung mit sich. Diese betrifft nicht nur die Einstellung, sondern auch Onboarding, Orientierung, Integration und die praktische Begleitung in der Anfangsphase.",
  },
  {
    title: "Integration",
    text: "Nachhaltige Fachkräftegewinnung gelingt nur, wenn Menschen im Unternehmen und im neuen Umfeld tatsächlich ankommen können. Dazu gehören Willkommenskultur, klare Ansprechpartner, strukturierte Einarbeitung und ein integrationsorientiertes Umfeld.",
  },
  {
    title: "Verlässlichkeit",
    text: "Menschen, die in einem neuen Land beruflich neu beginnen, brauchen klare Informationen, belastbare Zusagen und nachvollziehbare Prozesse. Verlässlichkeit ist deshalb ein zentraler Maßstab unserer Arbeit.",
  },
  {
    title: "Lebensnahe Umsetzung",
    text: "Ein tragfähiger Start in Europa setzt voraus, dass auch die praktische Lebensrealität berücksichtigt wird. Dazu zählen insbesondere die ersten Wochen nach Ankunft, finanzielle Übergangsphasen sowie die Frage nach Wohnraum oder konkreter Unterstützung bei der Wohnraumsuche.",
  },
  {
    title: "Nachhaltigkeit",
    text: "Unser Ziel ist nicht die kurzfristige Besetzung offener Stellen, sondern eine stabile Verbindung zwischen Fachkraft, Arbeitgeber und neuem Lebensumfeld. Nachhaltigkeit bedeutet für uns Passung, Vorbereitung und langfristige Perspektive.",
  },
];

const focusPoints = [
  "Faire Bedingungen",
  "Gute Vorbereitung",
  "Respekt vor Qualifikation",
  "Echte Integrationsbereitschaft",
];

const commitments = [
  "Keine Rekrutierung ohne Vorbereitung",
  "Keine Geschwindigkeit auf Kosten von Klarheit",
  "Keine Auslagerung von Verantwortung",
  "Keine Trennung von Beruf und Ankommen",
];

export default function Leitbild() {
  useEffect(() => {
    document.title = "Leitbild | Experts4Europe";
  }, []);

  const hero = (
    <section className="public-leitbild-hero">
      <CContainer fluid className="public-core-container public-page-shell public-leitbild-shell">
        <div className="public-leitbild-hero__panel">
          <div className="public-leitbild-ambient" aria-hidden="true">
            <span className="public-leitbild-ambient__orbit public-leitbild-ambient__orbit--one" />
            <span className="public-leitbild-ambient__orbit public-leitbild-ambient__orbit--two" />
            <span className="public-leitbild-ambient__line public-leitbild-ambient__line--one" />
            <span className="public-leitbild-ambient__line public-leitbild-ambient__line--two" />
          </div>

          <div className="public-leitbild-hero__copy">
            <span className="public-page-overline public-page-overline--inverse">Leitbild</span>
            <h1
              className="public-leitbild-hero__title"
              style={{
                color: "#ffffff",
                WebkitTextFillColor: "#ffffff",
                opacity: 1,
              }}
            >
              <span>Verantwortungsvolle Wege</span>
              <span>für Fachkräfte und</span>
              <span>Arbeitgeber in Europa</span>
            </h1>
            <p className="public-leitbild-hero__lead">
              Experts4Europe steht für faire, vorbereitete und verantwortungsvolle internationale Fachkräftegewinnung in Europa.
            </p>

            <div className="public-leitbild-hero__signals">
              {focusPoints.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <aside className="public-leitbild-hero__aside">
            <article className="public-leitbild-note">
              <span className="public-page-overline public-page-overline--inverse">Mission</span>
              <p>Faire, gut vorbereitete und verantwortungsvolle Wege qualifizierter Fachkräfte aus Drittstaaten in den europäischen Arbeitsmarkt.</p>
            </article>
            <article className="public-leitbild-note">
              <span className="public-page-overline public-page-overline--inverse">Vision</span>
              <p>Internationale Fachkräftegewinnung in Europa transparenter, fairer und tragfähiger gestalten.</p>
            </article>
            <article className="public-leitbild-note">
              <span className="public-page-overline public-page-overline--inverse">Kurzfassung</span>
              <p>Im Mittelpunkt stehen faire Bedingungen, gute Vorbereitung, Respekt vor Qualifikation, echte Integrationsbereitschaft und tragfähige Perspektiven für beide Seiten.</p>
            </article>
          </aside>
        </div>
      </CContainer>
    </section>
  );

  return (
    <PublicSiteShell
      pageClassName="public-guiding-page public-guiding-page--leitbild public-leitbild-page"
      topBandTone="hero"
      hero={hero}
    >
      <section className="public-core-band public-core-band--leitbild-editorial">
        <CContainer fluid className="public-core-container public-page-shell public-leitbild-shell">
          <div className="public-leitbild-editorial">
            <div className="public-leitbild-editorial__copy">
              <span className="public-page-overline">Worum es uns geht</span>
              <h2 className="public-page-section-title">Internationale Fachkräftegewinnung braucht Haltung, die im Alltag trägt.</h2>
              <p className="public-page-body">
                Für uns beginnt Verantwortung nicht erst mit dem ersten Arbeitstag. Sie beginnt bei der Einordnung von Qualifikation, bei realistischer Vorbereitung, bei klaren Erwartungen und bei der Bereitschaft, Integration tatsächlich mitzudenken.
              </p>
              <p className="public-page-body public-page-body--muted">
                Das Leitbild von Experts4Europe ist deshalb kein Begleittext neben einem Vermittlungsprozess. Es beschreibt die Form, in der berufliche Wege nach Europa fair, verlässlich und tragfähig gestaltet werden sollen.
              </p>
            </div>

            <div className="public-leitbild-editorial__rail">
              <article className="public-leitbild-editorial__panel">
                <span className="public-page-overline">Im Mittelpunkt</span>
                <div className="public-leitbild-editorial__pillars">
                  {focusPoints.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>

              <article className="public-leitbild-editorial__panel">
                <span className="public-page-overline">Unser Anspruch</span>
                <ul className="public-leitbild-editorial__list">
                  {commitments.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-page-shell public-leitbild-shell">
          <div className="public-leitbild-manifesto">
            <article className="public-leitbild-manifesto__card public-leitbild-manifesto__card--mission">
              <span className="public-page-overline">Unsere Mission</span>
              <div className="public-page-richtext">
                <p>Experts4Europe steht für faire, gut vorbereitete und verantwortungsvolle Wege qualifizierter Fachkräfte aus Drittstaaten in den europäischen Arbeitsmarkt.</p>
                <p>Wir unterstützen qualifizierte Menschen dabei, ihre beruflichen Perspektiven in Europa strukturiert und realistisch zu entwickeln. Gleichzeitig begleiten wir Arbeitgeber bei einer internationalen Rekrutierung, die nicht nur auf Besetzung, sondern auf nachhaltige Integration ausgerichtet ist.</p>
                <p>Für uns umfasst dieser Weg mehr als die Vermittlung zwischen Bewerber und Arbeitgeber. Entscheidend sind auch die fachliche Einordnung vorhandener Qualifikationen, die Vorbereitung auf europäische Bewerbungs- und Arbeitsstandards, die soziale und betriebliche Integration sowie verlässliche Rahmenbedingungen für das Ankommen im neuen Umfeld.</p>
              </div>
            </article>

            <article className="public-leitbild-manifesto__card public-leitbild-manifesto__card--vision">
              <span className="public-page-overline">Unsere Vision</span>
              <div className="public-page-richtext">
                <p>Wir möchten dazu beitragen, dass internationale Fachkräftegewinnung in Europa transparenter, fairer und tragfähiger gestaltet wird.</p>
                <p>Qualifizierte Migration soll von Klarheit, Verlässlichkeit und gegenseitigem Respekt geprägt sein. Arbeitgeber sollen internationale Rekrutierung nicht als isolierten Beschaffungsprozess verstehen, sondern als verantwortungsvollen Gesamtprozess, der fachliche, menschliche und praktische Aspekte gleichermaßen berücksichtigt.</p>
              </div>
            </article>
          </div>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--leitbild-values">
        <CContainer fluid className="public-core-container public-page-shell public-leitbild-shell">
          <div className="public-leitbild-values">
            <div className="public-leitbild-values__head">
              <span className="public-page-overline public-page-overline--inverse">Unsere Werte</span>
              <h2 className="public-page-section-title">Die Werte von Experts4Europe sind nicht dekorativ. Sie bestimmen die Qualität des Weges.</h2>
            </div>

            <div className="public-leitbild-values__grid">
              {values.map((item, index) => (
                <article
                  key={item.title}
                  className={`public-leitbild-value-card ${
                    index < 2 ? "public-leitbild-value-card--featured" : ""
                  }`.trim()}
                >
                  <h3 className="public-page-subtitle">{item.title}</h3>
                  <p className="public-page-body">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--leitbild-closure">
        <CContainer fluid className="public-core-container public-page-shell public-leitbild-shell">
          <div className="public-leitbild-closure">
            <div className="public-leitbild-closure__copy">
              <span className="public-page-overline public-page-overline--inverse">Unser Anspruch</span>
              <h2 className="public-page-section-title">Faire Einwanderung braucht Klarheit, Vorbereitung und Verantwortung.</h2>
              <p className="public-page-body">
                Genau daran arbeitet Experts4Europe: an einem ruhigen, verlässlichen und würdevollen Weg für Fachkräfte und Arbeitgeber in Europa.
              </p>
            </div>

            <div className="public-leitbild-closure__actions">
              <Link to="/roadmap" className="public-leitbild-closure__action public-leitbild-closure__action--light">
                Roadmap ansehen
              </Link>
              <Link to="/register" className="public-leitbild-closure__action public-leitbild-closure__action--solid">
                Registrieren
              </Link>
              <Link to="/" className="public-leitbild-closure__link">
                Zur Startseite
              </Link>
            </div>
          </div>
        </CContainer>
      </section>
    </PublicSiteShell>
  );
}
